// ---------------------------------------------------------------------------
// Session and permissions
// ---------------------------------------------------------------------------
// Two halves, kept distinct on purpose:
//
//   * Cognito answers "who are you" — the sign-in flow, the challenges, the
//     tokens. Normal sign-in stays between the browser and Cognito. Password
//     recovery completion goes through the API with Cognito's one-time email
//     code so the old authenticator can be reset only after the code is proven.
//   * The support API answers "who are you HERE" — the role your groups
//     confer, the permission list, your organization and plan.
//
// So a successful Cognito sign-in is not yet a session: the profile fetch that
// follows it is. An account that authenticates but holds no support role gets
// a 401 from that fetch, and is told so rather than dropped into an empty
// console.
//
// The UI hiding a control is a courtesy, not a security boundary. Every check
// here has a matching check on the API; this exists so people are not shown
// buttons that will fail.
// ---------------------------------------------------------------------------

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  confirmSignIn,
  fetchAuthSession,
  resetPassword,
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
} from 'aws-amplify/auth';
import api, { ApiError } from '../api/client';
import { isCognitoConfigured } from './cognito';

const AuthContext = createContext(null);

/**
 * What Cognito is asking for next, in the words the sign-in screen uses.
 *
 * Amplify's step names are precise but not presentable, and the screen should
 * not be pattern-matching on strings from a dependency.
 */
export function challengeFrom(step) {
  switch (step?.signInStep) {
    case 'DONE':
      return null;
    case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
      return { kind: 'new-password' };
    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
      return { kind: 'totp' };
    case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
      return {
        kind: 'totp-setup',
        // The shared secret, for the authenticator app. Shown as a QR code and
        // as text, because not every authenticator scans.
        secret: step.totpSetupDetails?.sharedSecret ?? null,
        uri: step.totpSetupDetails?.getSetupUri?.('CliniqVoice Support')?.toString() ?? null,
      };
    case 'RESET_PASSWORD':
      return { kind: 'reset-required' };
    default:
      // An unmapped step is a flow this console does not implement. Naming it
      // beats a blank screen, and it tells whoever reads the report what to add.
      return { kind: 'unsupported', step: step?.signInStep ?? 'unknown' };
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [meta, setMeta] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | authenticated | anonymous
  const [error, setError] = useState(null);

  /**
   * Load the profile for whatever Cognito session exists.
   *
   * Called on boot and after every completed sign-in, so there is exactly one
   * path by which someone becomes authenticated in this app.
   */
  const loadSession = useCallback(async () => {
    if (!isCognitoConfigured) {
      setStatus('anonymous');
      return null;
    }

    try {
      const [{ user: me }, metaData] = await Promise.all([api.me(), api.meta()]);
      setUser(me);
      setMeta(metaData);
      setError(null);
      setStatus('authenticated');
      return me;
    } catch (err) {
      if (err instanceof ApiError && err.isAuthError) {
        // Either there is no Cognito session, or there is one that confers no
        // support role. Those need different words, and only the first is
        // ordinary — so ask Cognito which it was.
        let hasTokens;
        try {
          hasTokens = Boolean((await fetchAuthSession()).tokens?.idToken);
        } catch {
          hasTokens = false;
        }

        setUser(null);
        setStatus('anonymous');
        setError(
          hasTokens
            ? new ApiError(
                'You are signed in, but your account has not been given a support role. ' +
                  'Ask an administrator to add you to the right group.',
                { status: 403 },
              )
            : null,
        );
        return null;
      }

      // A failure that is not a 401 is a real outage, and saying "please sign
      // in" would send people round a loop that cannot succeed.
      setError(err);
      setStatus('anonymous');
      return null;
    }
  }, []);

  useEffect(() => {
    // loadSession sets state only after awaiting the network, so this is not a
    // synchronous setState in an effect body — but the lint rule follows the
    // call and cannot see the await boundary.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadSession();
  }, [loadSession]);

  /**
   * Begin sign-in.
   *
   * @returns {Promise<{done: boolean, challenge: object|null}>} `done` when
   *   the session is live and the profile has loaded; otherwise the challenge
   *   the screen has to satisfy next.
   */
  const signIn = useCallback(
    async (email, password) => {
      setError(null);

      // A stale session left over from a previous sign-in makes Cognito reject
      // a fresh attempt outright. Clearing first turns "there is already a
      // signed in user" into an ordinary sign-in.
      await cognitoSignOut().catch(() => {});

      const step = (await cognitoSignIn({ username: email, password })).nextStep;
      const challenge = challengeFrom(step);

      if (!challenge) {
        await loadSession();
        return { done: true, challenge: null };
      }
      return { done: false, challenge };
    },
    [loadSession],
  );

  /**
   * Answer whatever Cognito asked for — a new password, a TOTP code, the code
   * that confirms an authenticator was enrolled. One function for all of them,
   * because Amplify models them as one step and the screen already knows which
   * it is showing.
   */
  const respondToChallenge = useCallback(
    async (response) => {
      const step = (await confirmSignIn({ challengeResponse: response })).nextStep;
      const challenge = challengeFrom(step);

      if (!challenge) {
        await loadSession();
        return { done: true, challenge: null };
      }
      return { done: false, challenge };
    },
    [loadSession],
  );

  /**
   * Ask Cognito to email a reset code.
   *
   * Self-service on purpose. The pool's recovery mechanism is the verified
   * email address (terraform/cognito.tf), so an agent who has locked themselves
   * out is not an identity question anyone has to adjudicate — and a reset that
   * needs an administrator is a reset that waits until Monday.
   *
   * The app client sets `prevent_user_existence_errors`, so an address with no
   * account answers exactly like one with an account, down to a simulated
   * delivery destination. The screen must not turn that into a claim about
   * whether the address is real, and neither does this.
   *
   * @returns {Promise<{done: boolean, destination: string|null}>} `done` when
   *   Cognito wants no code at all; otherwise where it says the code went.
   */
  const requestPasswordReset = useCallback(async (email) => {
    setError(null);
    const { nextStep } = await resetPassword({ username: email });
    return {
      done: nextStep?.resetPasswordStep === 'DONE',
      destination: nextStep?.codeDeliveryDetails?.destination ?? null,
    };
  }, []);

  /**
   * Set a new password with the emailed code and clear the old authenticator.
   *
   * The server only clears MFA after Cognito accepts the code and password.
   * Do not sign in automatically: the next sign-in must enroll a fresh
   * authenticator before access is granted.
   */
  const confirmPasswordReset = useCallback(async ({ email, code, newPassword }) => {
    setError(null);
    await api.confirmPasswordReset({ email, code, newPassword });
  }, []);

  const signOut = useCallback(async () => {
    try {
      await cognitoSignOut();
    } finally {
      // Clear locally regardless. If the call failed the tokens may still be
      // gone, and leaving a stale identity on screen is worse.
      setUser(null);
      setError(null);
      setStatus('anonymous');
    }
  }, []);

  const refresh = useCallback(async () => {
    const { user: me } = await api.me();
    setUser(me);
    return me;
  }, []);

  const value = useMemo(() => {
    const permissions = new Set(user?.permissions ?? []);
    return {
      user,
      meta,
      status,
      error,
      signIn,
      respondToChallenge,
      requestPasswordReset,
      confirmPasswordReset,
      signOut,
      refresh,
      loadSession,
      isInternal: Boolean(user?.is_internal),
      /** Does the signed-in user hold this permission? */
      can: (permission) => permissions.has(permission),
      /** Any of these? */
      canAny: (list) => list.some((p) => permissions.has(p)),
    };
  }, [
    user,
    meta,
    status,
    error,
    signIn,
    respondToChallenge,
    requestPasswordReset,
    confirmPasswordReset,
    signOut,
    refresh,
    loadSession,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an AuthProvider.');
  return ctx;
}

/** Enum labels and option lists from the server, so a dropdown cannot drift. */
export function useMeta() {
  const { meta } = useAuth();
  return meta;
}
